import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link
              to="/"
              onClick={closeMenu}
              className={`font-heading text-xl md:text-2xl transition-colors duration-300 ${
                scrolled ? 'text-foreground-900' : 'text-white'
              }`}
            >
              SuperbuySpread
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Home
              </Link>
              <Link
                to="/superbuy-spreadsheet-complete-guide"
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Complete Guide
              </Link>
              <Link
                to="/superbuy-spreadsheet-templates"
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Templates
              </Link>
              <Link
                to="/superbuy-spreadsheet-tutorial"
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`}
              >
                Tutorial
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30'
                }`}
              >
                Buy It Now
                <i className="ri-arrow-right-up-line ml-1.5"></i>
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="Open menu"
            >
              <i className={`ri-menu-line text-2xl ${scrolled ? 'text-foreground-800' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeMenu}
          ></div>

          <div className="absolute top-0 right-0 h-full w-[300px] max-w-[85vw] bg-background-50 shadow-2xl animate-slide-in-right">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
                <Link
                  to="/"
                  onClick={closeMenu}
                  className="font-heading text-lg text-foreground-900"
                >
                  SuperbuySpread
                </Link>
                <button
                  onClick={closeMenu}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/50 text-foreground-600 hover:bg-background-300/50 transition-colors cursor-pointer"
                  aria-label="Close menu"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4">
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                    Navigation
                  </h4>
                  <div className="flex flex-col gap-1">
                    <Link
                      to="/"
                      onClick={closeMenu}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-home-4-line text-base"></i>
                      Home
                    </Link>
                    <Link
                      to="/superbuy-spreadsheet-complete-guide"
                      onClick={closeMenu}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-book-open-line text-base"></i>
                      Complete Guide
                    </Link>
                    <Link
                      to="/superbuy-spreadsheet-templates"
                      onClick={closeMenu}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-file-copy-line text-base"></i>
                      Free Templates
                    </Link>
                    <Link
                      to="/superbuy-spreadsheet-tutorial"
                      onClick={closeMenu}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-base"></i>
                      Beginner Tutorial
                    </Link>
                    <Link
                      to="/superbuy-spreadsheet-safety"
                      onClick={closeMenu}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-shield-check-line text-base"></i>
                      Safety Guide
                    </Link>
                    <Link
                      to="/superbuy-spreadsheet-comparison"
                      onClick={closeMenu}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-scales-line text-base"></i>
                      Comparison
                    </Link>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                    Shop Categories
                  </h4>
                  <div className="flex flex-col gap-0.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMenu}
                        className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <span>{cat.name}</span>
                        <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 border-t border-background-200/60">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMenu}
                  className="flex items-center justify-center w-full px-5 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line ml-1.5"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}